import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User, LoginRequest, RegisterRequest, TokenResponse } from "@/types/user";
import type { MenuItem } from "@/types/admin";
import authService from "@/services/authService";
import { getAccessToken, clearTokens, syncTokensFromNative, saveTokensToNative, clearNativeTokens, isAuthExpiredError, AUTH_EXPIRED_EVENT } from "@/services/api";
import { initializePush, unsubscribeFromPush } from "@/services/pushService";
import { queryClient } from "@/lib/queryClient";
import { isEmployee } from "@/constants/userTypes";
import requiredDocumentService from "@/services/requiredDocumentService";
import { worklogService } from "@/services/worklogService";
import widgetService from "@/services/widgetService";

// 위젯 코드 → 해당 위젯이 진입하는 페이지 경로 매핑
// 위젯 권한은 헤더 아이콘 표시뿐 아니라 해당 페이지(라우트) 접근 권한도 부여한다.
// (이 메뉴들은 company_menus 메뉴 트리에 포함되지 않는 아이콘형 기능이므로
//  menu 권한이 아닌 위젯 권한으로 접근을 허용한다.)
const WIDGET_PATHS: Record<string, string> = {
  "sticky-notes": "/sticky-notes",
  calendar: "/calendar",
  chat: "/chat",
};

// 메뉴로 등록되지 않았지만 특정 메뉴의 부속 화면인 경로 → 요구 메뉴 경로
// (해당 메뉴 권한이 있어야만 들어갈 수 있다. 예: 징계 기준 설정 = 징계 관리 권한)
const ROUTE_MENU_MAP: Record<string, string> = {
  "/discipline/standards": "/discipline/admin", // 징계 관리 화면에서 진입
  "/discipline/stats": "/discipline/admin", // 징계 관리 통계 버튼
  // 전자결재 v2 (EA) 부속 화면 + 구 경로 리다이렉트 통과
  "/ea/documents": "/ea/inbox", // 문서 상세·수정·인쇄
  "/approval": "/ea/inbox", // 구 /approval/* → EaLegacyRedirect
  "/approval-management": "/ea/processing",
  "/purchase-request": "/ea/inbox",
  "/card-approval": "/ea/inbox",
  "/expense": "/ea/inbox",
  "/competency/report": "/competency/admin", // 역량 관리에서 리포트 열람
  "/tasks/employee-gantt": "/my-tasks", // 내 업무에서 간트 보기
  "/kpi/team": "/kpi/my", // 내 성과 점수(super_admin 전용 메뉴)에서 팀 성과 열람
  "/kpi/rewards": "/kpi/my", // 내 성과 점수(super_admin 전용 메뉴)에서 성과급 재원·배분
};

// 메뉴 트리에서 모든 경로를 추출하는 헬퍼 함수
function extractAllPaths(menus: MenuItem[]): Set<string> {
  const paths = new Set<string>();

  const traverse = (items: MenuItem[]) => {
    for (const item of items) {
      if (item.path) {
        paths.add(item.path);
        // 하위 경로도 허용 (예: /admin -> /admin/users)
        // 단, 정확한 경로 매칭을 위해 path 자체만 저장
      }
      if (item.children && item.children.length > 0) {
        traverse(item.children);
      }
    }
  };

  traverse(menus);
  return paths;
}

// 메뉴 트리에서 첫 번째 유효한 경로를 찾는 헬퍼 함수
function findFirstValidPath(menus: MenuItem[]): string | null {
  for (const item of menus) {
    // 활성 메뉴이고 유효한 경로가 있으면 반환
    if (item.is_active && item.path) {
      return item.path;
    }
    // 자식 메뉴가 있으면 재귀적으로 탐색
    if (item.children && item.children.length > 0) {
      const childPath = findFirstValidPath(item.children);
      if (childPath) {
        return childPath;
      }
    }
  }
  return null;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  accessibleMenus: MenuItem[];
  accessiblePaths: string[];
  authorizedPaths: string[]; // 인가 기준 메뉴 경로 (/auth/me/accessible-paths)
  authorizedPathsLoaded: boolean; // 인가 경로 수신 여부 (미수신 시 사이드바 경로로 폴백)
  accessibleWidgets: string[]; // 위젯 권한 코드 목록 (상속 포함)
  passwordChangeRequired: boolean;
  onboardingRequired: boolean; // 직원 개인정보 온보딩
  companyOnboardingRequired: boolean; // 회사 초기 설정 온보딩 (admin만)
  consentRequired: boolean; // 개인정보수집동의 필요
  documentSubmissionRequired: boolean; // 필수 서류 미제출
  docSkipDurationMinutes: number; // 건너뛰기 유효 시간 (분)
  isPastDeadline: boolean; // 서류 제출 마감일 경과 여부
  hasActiveExtension: boolean; // 활성 연장 여부
  extensionExpiresAt: string | null; // 연장 만료 시각
  noCompany: boolean; // 소속된 기업이 없는 경우
  noPermissionGroup: boolean; // 권한 그룹이 설정되지 않은 경우
  showDeviceNamePrompt: boolean; // 새 기기 이름 입력 프롬프트
  dailyReportRequired: boolean; // 일일 업무 보고 익일 차단(마감 경과 미등록 보유)
  dailyReportSkipMinutes: number; // 차단 화면 건너뛰기 허용 시간(분), 0=불가

  // Actions
  login: (data: LoginRequest) => Promise<TokenResponse>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  fetchCurrentUser: (skipOnboardingCheck?: boolean) => Promise<void>;
  fetchAccessibleMenus: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  clearError: () => void;
  clearPasswordChangeRequired: () => void;
  clearOnboardingRequired: () => void;
  clearCompanyOnboardingRequired: () => void;
  clearConsentRequired: () => void;
  clearDocumentSubmissionRequired: () => void;
  clearDailyReportRequired: () => void;
  refreshDailyReportStatus: () => Promise<void>;
  dismissDeviceNamePrompt: () => void;
  initialize: () => Promise<void>;
  hasMenuAccess: (path: string) => boolean;
  hasGrantedMenuPath: (path: string) => boolean;
  getDefaultLandingPath: () => string;
  switchCompany: (companyCode: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      accessibleMenus: [],
      accessiblePaths: [],
      authorizedPaths: [],
      authorizedPathsLoaded: false,
      accessibleWidgets: [],
      passwordChangeRequired: false,
      onboardingRequired: false,
      companyOnboardingRequired: false,
      consentRequired: false,
      documentSubmissionRequired: false,
      dailyReportRequired: false,
      dailyReportSkipMinutes: 0,
      docSkipDurationMinutes: 30,
      isPastDeadline: false,
      hasActiveExtension: false,
      extensionExpiresAt: null,
      noCompany: false,
      noPermissionGroup: false,
      showDeviceNamePrompt: false,

      login: async (data: LoginRequest) => {
        set({ isLoading: true, error: null });
        try {
          // 1. 로그인 API 호출 (토큰만 받아옴, 아직 저장 안함)
          const tokenResponse = await authService.login(data);

          // 2. 소속 기업이 없는 경우 처리
          if (tokenResponse.no_company) {
            const { setAccessToken, setRefreshToken } = await import("@/services/api");
            setAccessToken(tokenResponse.access_token);
            setRefreshToken(tokenResponse.refresh_token);

            // 제한된 사용자 정보 설정 (getCurrentUser 호출하지 않음)
            set({
              user: null,
              isAuthenticated: true,
              isLoading: false,
              passwordChangeRequired: false,
              onboardingRequired: false,
              noCompany: true,
            });

            return tokenResponse;
          }

          // 2. axios 인스턴스에만 토큰을 임시로 설정 (localStorage는 아직 안함)
          const api = (await import("@/services/api")).api;
          api.defaults.headers.common["Authorization"] = `Bearer ${tokenResponse.access_token}`;

          // 3. 사용자 정보 + 메뉴 권한을 병렬 로드
          const [user] = await Promise.all([
            authService.getCurrentUser(),
            get().fetchAccessibleMenus(),
          ]);

          // 5. 토큰을 localStorage에 저장
          const { setAccessToken, setRefreshToken } = await import("@/services/api");
          setAccessToken(tokenResponse.access_token);
          setRefreshToken(tokenResponse.refresh_token);

          // 5.5. 기업 정보를 localStorage에 저장 (헤더 브랜딩용)
          if (tokenResponse.company_name) {
            localStorage.setItem("company_name", tokenResponse.company_name);
          }
          if (tokenResponse.company_logo_url) {
            localStorage.setItem("company_logo", tokenResponse.company_logo_url);
          } else {
            localStorage.removeItem("company_logo");
          }

          // 6. WebView 환경이면 네이티브 저장소에도 저장
          const isWebView = typeof window !== 'undefined' && !!(window as unknown as { isReactNativeWebView?: boolean }).isReactNativeWebView;
          if (isWebView && tokenResponse.access_token) {
            saveTokensToNative(tokenResponse.access_token, tokenResponse.refresh_token);
          }

          // 7. 온보딩 필요 여부 체크: 직원(employee, external_staff)만 해당
          const needsOnboarding =
            isEmployee(user.user_type) &&
            (!user.english_name ||
            !user.personal_email ||
            !user.phone ||
            !user.address ||
            !user.hire_date ||
            !user.job_family ||
            !user.job_title ||
            user.previous_experience_months == null ||
            !user.emergency_contact_name ||
            !user.emergency_contact_phone ||
            !user.emergency_contact_relation);

          // 8. 동의 필요 여부 체크: 직원만 해당, consent_version 불일치 시
          //    required_consent_version이 없으면 동의 불필요 (API 미지원 fallback)
          const needsConsent =
            isEmployee(user.user_type) &&
            !!user.required_consent_version &&
            user.consent_version !== user.required_consent_version;

          // 8.5. 회사 온보딩 필요 여부 체크: admin 역할만 해당
          const needsCompanyOnboarding = user.company_onboarding_required || false;

          console.log('[authStore] login - User data:', {
            email: user.email,
            role: user.role,
            company_id: user.company_id,
            company_onboarding_required: user.company_onboarding_required,
            needsCompanyOnboarding
          });

          // 9. 상태 업데이트
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            passwordChangeRequired: tokenResponse.password_change_required,
            onboardingRequired: needsOnboarding,
            companyOnboardingRequired: needsCompanyOnboarding,
            consentRequired: needsConsent,
            documentSubmissionRequired: false,
            dailyReportRequired: false,
            noCompany: false,
            showDeviceNamePrompt: tokenResponse.is_new_device || false,
          });

          // 9. 푸시 알림 초기화 (비차단)
          if (!isWebView) {
            initializePush().catch(console.error);
          }

          // 10. 필수 서류 미제출 체크 (비차단 - 네비게이션 지연 방지)
          if (!needsOnboarding && isEmployee(user.user_type)) {
            get().refreshDailyReportStatus();
            requiredDocumentService.checkMandatory().then((mandatoryCheck) => {
              if (mandatoryCheck.has_pending_mandatory) {
                set({
                  documentSubmissionRequired: true,
                  docSkipDurationMinutes: mandatoryCheck.skip_duration_minutes ?? 30,
                  isPastDeadline: mandatoryCheck.is_past_deadline ?? false,
                  hasActiveExtension: mandatoryCheck.has_active_extension ?? false,
                  extensionExpiresAt: mandatoryCheck.extension_expires_at ?? null,
                });
              }
            }).catch((e) => {
              console.warn('[authStore] Failed to check mandatory documents:', e);
            });
          }

          return tokenResponse;
        } catch (error) {
          console.error('[authStore] Login failed:', error);

          // 로그인 실패 시 모든 토큰과 상태 즉시 삭제
          const api = (await import("@/services/api")).api;
          delete api.defaults.headers.common["Authorization"];
          clearTokens();

          // 상태를 먼저 초기화
          set({
            error: error instanceof Error ? error.message : "로그인에 실패했습니다.",
            isLoading: false,
            isAuthenticated: false,
            user: null,
            accessibleMenus: [],
            accessiblePaths: [],
            passwordChangeRequired: false,
            onboardingRequired: false,
            consentRequired: false,
            documentSubmissionRequired: false,
            dailyReportRequired: false,
          });

          // persist 스토리지 완전 삭제 (zustand의 persist가 자동 저장하기 전에)
          localStorage.removeItem('auth-storage');

          throw error;
        }
      },

      register: async (data: RegisterRequest) => {
        set({ isLoading: true, error: null });
        try {
          await authService.register(data);

          // 회원가입 성공 - authService.register에서 자동 로그인 시도는 내부에서 처리됨
          // 승인 대기 중인 사용자는 로그인이 실패하므로 여기서는 단순히 완료 처리
          set({ isLoading: false });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : "회원가입에 실패했습니다.";
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        const isWebView = typeof window !== 'undefined' && !!(window as unknown as { isReactNativeWebView?: boolean }).isReactNativeWebView;

        set({ isLoading: true });

        // WebView에서는 네이티브 저장소도 클리어
        if (isWebView) {
          console.log('[AuthStore] WebView logout - clearing native tokens');

          // 네이티브 저장소 토큰 삭제
          clearNativeTokens();

          // Clear React Query cache
          queryClient.clear();

          // Clear auth state and localStorage
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            accessibleMenus: [],
            accessiblePaths: [],
            passwordChangeRequired: false,
          });
          clearTokens();

          // Clear company branding info from localStorage
          localStorage.removeItem("company_name");
          localStorage.removeItem("company_logo");

          // Send LOGOUT message to native app (for additional cleanup like navigation)
          const ReactNativeWebView = (window as unknown as { ReactNativeWebView?: { postMessage: (msg: string) => void } }).ReactNativeWebView;
          if (ReactNativeWebView) {
            console.log('[AuthStore] Sending LOGOUT message to native app');
            ReactNativeWebView.postMessage(JSON.stringify({ type: 'LOGOUT' }));
          }
          return;
        }

        // 일반 웹에서는 기존 로직 유지
        try {
          // Unsubscribe from push notifications before logout
          await unsubscribeFromPush().catch(console.error);
          await authService.logout();
        } finally {
          // Clear React Query cache to prevent showing previous user's data
          queryClient.clear();

          // Clear auth state
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            accessibleMenus: [],
            accessiblePaths: [],
            passwordChangeRequired: false,
          });

          // Clear company branding info from localStorage
          localStorage.removeItem("company_name");
          localStorage.removeItem("company_logo");
        }
      },

      fetchCurrentUser: async (skipOnboardingCheck = false) => {
        try {
          const user = await authService.getCurrentUser();

          // 온보딩 필요 여부 체크: 직원(employee, external_staff)만 해당
          // skipOnboardingCheck가 true이면 현재 상태 유지
          const currentState = get();

          // 디버깅: 온보딩 필수 필드 체크
          if (isEmployee(user.user_type) && !skipOnboardingCheck) {
            const missingFields = [];
            if (!user.english_name) missingFields.push('english_name');
            if (!user.personal_email) missingFields.push('personal_email');
            if (!user.phone) missingFields.push('phone');
            if (!user.address) missingFields.push('address');
            if (!user.hire_date) missingFields.push('hire_date');
            if (!user.job_family) missingFields.push('job_family');
            if (!user.job_title) missingFields.push('job_title');
            if (user.previous_experience_months == null) missingFields.push('previous_experience_months');
            if (!user.emergency_contact_name) missingFields.push('emergency_contact_name');
            if (!user.emergency_contact_phone) missingFields.push('emergency_contact_phone');
            if (!user.emergency_contact_relation) missingFields.push('emergency_contact_relation');

            if (missingFields.length > 0) {
              console.log('⚠️ 온보딩 필요 - 누락된 필드:', missingFields);
            }
          }

          const needsOnboarding = skipOnboardingCheck
            ? currentState.onboardingRequired  // 현재 상태 유지
            : isEmployee(user.user_type) &&
              (!user.english_name ||
              !user.personal_email ||
              !user.phone ||
              !user.address ||
              !user.hire_date ||
              !user.job_family ||
              !user.job_title ||
              user.previous_experience_months == null ||
              !user.emergency_contact_name ||
              !user.emergency_contact_phone ||
              !user.emergency_contact_relation);

          const needsConsent =
            isEmployee(user.user_type) &&
            !!user.required_consent_version &&
            user.consent_version !== user.required_consent_version;

          // 회사 온보딩 필요 여부 체크: admin 역할만 해당
          const needsCompanyOnboarding = user.company_onboarding_required || false;

          console.log('[authStore] fetchCurrentUser - User data:', {
            email: user.email,
            role: user.role,
            company_id: user.company_id,
            company_onboarding_required: user.company_onboarding_required,
            needsCompanyOnboarding
          });

          set({
            user,
            isAuthenticated: true,
            onboardingRequired: needsOnboarding,
            companyOnboardingRequired: needsCompanyOnboarding,
            consentRequired: needsConsent,
          });

          console.log('[authStore] State updated - companyOnboardingRequired:', needsCompanyOnboarding);
        } catch (e) {
          // 진짜 인증 만료(401/403)일 때만 세션을 정리한다.
          // 네트워크·서버 일시 장애로는 로그아웃하지 않고 기존 세션을 유지해
          // "로그인돼 있는데 /login 폼이 보이는" 잘못된 강제 로그아웃을 방지한다.
          if (isAuthExpiredError(e)) {
            set({ user: null, isAuthenticated: false });
            clearTokens();
          }
        }
      },

      fetchAccessibleMenus: async () => {
        try {
          const { user } = get();
          const [menus, widgets, authorized] = await Promise.all([
            authService.getMyMenus(),
            // 위젯 권한 조회 실패가 메뉴 로딩을 막지 않도록 개별 fallback
            widgetService.getMyWidgets().catch(() => [] as string[]),
            // 인가 기준 경로 (라우트 가드용). 실패 시 null → 사이드바 경로로 폴백
            authService.getMyAccessiblePaths().catch(() => null),
          ]);

          // super_admin이 아닌 경우에만 권한 그룹 체크
          if (user && user.role !== "super_admin" && user.role !== "admin") {
            if (menus.length === 0) {
              set({
                noPermissionGroup: true,
                accessibleMenus: [],
                accessiblePaths: [],
                authorizedPaths: authorized ?? [],
                authorizedPathsLoaded: authorized !== null,
                accessibleWidgets: widgets,
              });
              return;
            }
          }

          const paths = extractAllPaths(menus);
          set({
            accessibleMenus: menus,
            accessiblePaths: Array.from(paths),
            authorizedPaths: authorized ?? [],
            authorizedPathsLoaded: authorized !== null,
            accessibleWidgets: widgets,
            noPermissionGroup: false,
          });
        } catch {
          // 메뉴 조회 실패 시 빈 배열로 설정
          set({
            accessibleMenus: [],
            accessiblePaths: [],
            authorizedPaths: [],
            authorizedPathsLoaded: false,
            accessibleWidgets: [],
          });
        }
      },

      updateProfile: async (data: Partial<User>) => {
        set({ isLoading: true, error: null });
        try {
          const user = await authService.updateProfile(data);
          set({ user, isLoading: false });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : "프로필 수정에 실패했습니다.";
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      clearError: () => set({ error: null }),

      clearPasswordChangeRequired: () => set({ passwordChangeRequired: false }),

      clearOnboardingRequired: () => set({ onboardingRequired: false }),
      clearCompanyOnboardingRequired: () => set({ companyOnboardingRequired: false }),

      clearConsentRequired: () => set({ consentRequired: false }),

      clearDocumentSubmissionRequired: () => set({ documentSubmissionRequired: false }),

      clearDailyReportRequired: () => set({ dailyReportRequired: false }),

      // 일일 업무 보고 익일 차단 상태 재조회 (로그인·세션 복원 시). 조회 실패는 차단하지 않음
      refreshDailyReportStatus: async () => {
        try {
          const st = await worklogService.getRequiredStatus();
          set({
            dailyReportRequired: !!(st.enabled && st.block_next_day_active),
            dailyReportSkipMinutes: st.block_skip_minutes ?? 0,
          });
        } catch {
          // ignore
        }
      },

      dismissDeviceNamePrompt: () => set({ showDeviceNamePrompt: false }),

      initialize: async () => {
        // Check if running in React Native WebView
        const isWebView = typeof window !== 'undefined' && !!(window as unknown as { isReactNativeWebView?: boolean }).isReactNativeWebView;

        // If in WebView, try to sync tokens from native storage
        if (isWebView) {
          console.log('[AuthStore] Running in React Native WebView');
          // 1. 먼저 짧은 대기 (기존 localStorage 주입 대기)
          await new Promise(resolve => setTimeout(resolve, 100));

          // 2. localStorage에 토큰이 없으면 네이티브 저장소에서 복구 시도
          if (!getAccessToken()) {
            console.log('[AuthStore] No token in localStorage, syncing from native...');
            await syncTokensFromNative();
          }
        }

        const token = getAccessToken();
        if (token) {
          // no_company(제한) 토큰이면 사용자/메뉴 조회를 건너뛰고 로비 상태로 유지한다.
          // 제한 토큰으로 /users/me 등 테넌트 API를 호출하면 403이 나고, 아래 catch가
          // 이를 인증 만료로 간주해 세션을 정리 → 새로고침 시 로그아웃되던 문제를 방지.
          try {
            const payload = JSON.parse(atob(token.split(".")[1]));
            if (payload && (payload.user_type === "no_company" || payload.role === "guest")) {
              set({ user: null, isAuthenticated: true, noCompany: true, isLoading: false });
              return;
            }
          } catch {
            // 토큰 파싱 실패는 무시하고 일반 흐름으로 진행
          }

          try {
            // 1. 사용자 정보는 먼저 가져와야 함 (메뉴 조회에 role 필요)
            await get().fetchCurrentUser();

            // 2. 메뉴 조회 + 필수 서류 체크 + 푸시 초기화를 병렬로 실행
            const { user: currentUser, onboardingRequired: needsOnboarding } = get();
            const parallelTasks: Promise<void>[] = [
              get().fetchAccessibleMenus(),
            ];

            // 필수 서류 미제출 체크 (병렬)
            if (currentUser && !needsOnboarding && isEmployee(currentUser.user_type)) {
              parallelTasks.push(get().refreshDailyReportStatus());
              parallelTasks.push(
                requiredDocumentService.checkMandatory().then(mandatoryCheck => {
                  set({
                    documentSubmissionRequired: mandatoryCheck.has_pending_mandatory,
                    docSkipDurationMinutes: mandatoryCheck.skip_duration_minutes ?? 30,
                    isPastDeadline: mandatoryCheck.is_past_deadline ?? false,
                    hasActiveExtension: mandatoryCheck.has_active_extension ?? false,
                    extensionExpiresAt: mandatoryCheck.extension_expires_at ?? null,
                  });
                }).catch(() => {
                  // Silently ignore - not critical for initialization
                })
              );
            }


            await Promise.all(parallelTasks);
          } catch (e) {
            // 진짜 인증 만료(401/403)일 때만 캐시·토큰을 정리한다.
            // 네트워크·서버 일시 장애 시에는 캐시된(persist된) 세션을 유지해
            // 앱 로딩 중 일시 오류로 강제 로그아웃되는 것을 방지한다.
            if (isAuthExpiredError(e)) {
              set({
                user: null,
                isAuthenticated: false,
                accessibleMenus: [],
                accessiblePaths: [],
              });
              clearTokens();
            }
          }
        } else {
          // 토큰이 없으면 캐시된 인증 상태도 초기화
          set({
            user: null,
            isAuthenticated: false,
            accessibleMenus: [],
            accessiblePaths: [],
          });
        }
        set({ isLoading: false });

        // 푸시 초기화는 첫 화면을 막지 않도록 로딩 해제 뒤 백그라운드로 돌린다.
        // (서비스워커 ready·FCM 구독 대기가 외부망/첫 설치에서 길어져 흰 화면이 늘어지던 요인)
        if (!isWebView && get().isAuthenticated && getAccessToken()) {
          initializePush().catch(console.error);
        }
      },

      hasMenuAccess: (path: string): boolean => {
        const { user, accessiblePaths, accessibleWidgets, authorizedPaths, authorizedPathsLoaded } = get();

        // 인가 기준 경로(/auth/me/accessible-paths)를 우선 사용한다.
        // - super_admin: 삭제되지 않은 전체 메뉴 경로 → 무조건 통과시키지 않아도 막히지 않음
        // - 삭제된 메뉴 경로는 여기 없으므로 라우트도 함께 차단됨
        // 아직 못 받아온 경우에만 사이드바 트리 경로로 폴백(전면 잠금 방지).
        const grantedPaths = authorizedPathsLoaded ? authorizedPaths : accessiblePaths;

        // 위젯 권한으로 접근하는 아이콘형 페이지(포스트잇/캘린더 등)
        // 메뉴 트리(company_menus)에는 없지만 위젯 권한이 있으면 접근 허용
        for (const [widgetCode, widgetPath] of Object.entries(WIDGET_PATHS)) {
          if (
            (path === widgetPath || path.startsWith(widgetPath + "/")) &&
            accessibleWidgets.includes(widgetCode)
          ) {
            return true;
          }
        }

        // 항상 접근 가능한 경로들 (기본 경로)
        const alwaysAllowedPaths = [
          "/",
          "/profile",
          "/notifications",
          "/settings/notifications",
          "/desktop-app", // 메신저 앱 설치 페이지 (Windows 메신저 + Android APK, 채팅 목록 버튼)
          "/document-submission",
          // 구독·결제: 잠금 상태에서도 관리자가 결제하러 들어올 수 있어야 함
          "/billing",
        ];

        // 직원(이상)만 접근 가능한 공통 페이지
        // - /dashboard: 대시보드
        // - /board/notice: 공지사항 — 사이드바 메뉴엔 노출하지 않지만 직원이면 누구나 열람 가능.
        //   실제 노출되는 글은 백엔드 visibility/근무지/회사 필터로 제어됨(메뉴 권한과 무관).
        // 에러/안내 페이지는 항상 열려 있어야 한다 (차단 결과 화면 자체가 막히면 무한 루프)
        alwaysAllowedPaths.push("/forbidden");
        alwaysAllowedPaths.push("/no-permission-group");

        if (user?.user_type === "employee") {
          alwaysAllowedPaths.push("/dashboard");
          // 게시판: 메뉴로 통제하지 않고 직원 전체 허용(글 노출은 백엔드 visibility 필터)
          alwaysAllowedPaths.push("/board");
          // 퇴사·인수인계: 사이드바 상시 메뉴에서 내리고(프로필 진입점으로 이전) 라우트만 유지.
          //   진입 경로 = 프로필의 '퇴사 신청' 링크 · 인수인계 상단 배너 · 알림 딥링크.
          //   셀프 신청 노출 여부는 회사 설정으로 제어(백엔드 게이트) — 라우트 자체는 열어둠.
          alwaysAllowedPaths.push("/offboarding/my");
          // 징계위원회 심의: 사이드바 메뉴는 리더에게만 노출하지만, 일반 직원이
          // 위원으로 지정될 수 있으므로 라우트는 직원 전체에 허용(데이터는 백엔드에서
          // 위원 본인 배정 건만 반환 — 비위원은 빈 화면).
          alwaysAllowedPaths.push("/discipline/committee");
        }

        if (alwaysAllowedPaths.some(allowed => path === allowed || path.startsWith(allowed + "/"))) {
          return true;
        }

        // OS 공유하기(Web Share Target) 수신 화면: 목적지가 파일함/채팅방 두 곳이므로
        // 파일함 메뉴 또는 채팅 위젯 중 하나만 있어도 진입 허용 (ROUTE_MENU_MAP은 단일 메뉴 매핑이라 별도 처리)
        if (path === "/share-receive" || path.startsWith("/share-receive/")) {
          if (accessibleWidgets.includes("chat")) return true;
          return grantedPaths.some((p) => p === "/files" || "/files".startsWith(p + "/"));
        }

        // 메뉴 부속 화면: 매핑된 메뉴 권한이 있으면 허용
        for (const [routePrefix, requiredMenuPath] of Object.entries(ROUTE_MENU_MAP)) {
          if (path === routePrefix || path.startsWith(routePrefix + "/")) {
            return grantedPaths.some(
              (p) => p === requiredMenuPath || requiredMenuPath.startsWith(p + "/"),
            );
          }
        }

        // 인가된 메뉴 경로와 정확히 일치하거나 그 하위 경로인지 확인
        for (const allowedPath of grantedPaths) {
          if (path === allowedPath) {
            return true;
          }
          // 하위 경로 허용 (예: /maintenance -> /maintenance/tickets/3)
          if (path.startsWith(allowedPath + "/")) {
            return true;
          }
        }

        return false;
      },

      // 인가된 메뉴 경로를 '직접 보유'했는지 — 항상-허용 경로/위젯/부속화면 매핑을 거치지 않는 순수 권한 판정.
      // 열람은 전원 허용(/board 등)이지만 '관리' 기능은 메뉴 보유로 여는 화면용
      // (예: 공지사항 관리 메뉴 path=/board/notice → 공지 등록·수정·삭제 버튼 노출).
      // 관리자 역할 폴백은 호출 측에서 처리(백엔드 require_menu_or_admin 규칙과 동일하게).
      hasGrantedMenuPath: (path: string): boolean => {
        const { accessiblePaths, authorizedPaths, authorizedPathsLoaded } = get();
        const grantedPaths = authorizedPathsLoaded ? authorizedPaths : accessiblePaths;
        return grantedPaths.includes(path);
      },

      getDefaultLandingPath: (): string => {
        const { accessibleMenus } = get();
        // 접근 가능한 메뉴에서 첫 번째 유효한 경로 찾기
        const firstPath = findFirstValidPath(accessibleMenus);
        // 찾은 경로가 없으면 기본값으로 /profile 반환
        return firstPath || "/profile";
      },

      switchCompany: async (companyCode: string) => {
        set({ isLoading: true, error: null });
        try {
          // 1. 기업 전환 API 호출 (새 토큰 받음)
          const tokenResponse = await authService.switchCompany(companyCode);

          // 2. axios 인스턴스에 새 토큰 설정
          const { setAccessToken, setRefreshToken } = await import("@/services/api");
          setAccessToken(tokenResponse.access_token);
          setRefreshToken(tokenResponse.refresh_token);

          // 3. 사용자 정보 새로 가져오기
          const user = await authService.getCurrentUser();

          // 4. 메뉴 권한 새로 가져오기
          const menus = await authService.getMyMenus();
          const paths = Array.from(extractAllPaths(menus));
          const widgets = await widgetService.getMyWidgets().catch(() => [] as string[]);
          // 기업 전환 시 인가 경로도 새 기업 기준으로 다시 받는다
          const authorized = await authService.getMyAccessiblePaths().catch(() => null);

          // 5. 기업 정보를 localStorage에 저장 (Sidebar 브랜딩용)
          if (tokenResponse.company_name) {
            localStorage.setItem("company_name", tokenResponse.company_name);
          }
          if (tokenResponse.company_logo_url) {
            localStorage.setItem("company_logo", tokenResponse.company_logo_url);
          } else {
            // 로고가 없으면 기존 저장된 로고도 삭제
            localStorage.removeItem("company_logo");
          }
          // Sidebar가 localStorage 변경을 감지하도록 커스텀 이벤트 발생
          window.dispatchEvent(new CustomEvent('company-branding-updated', {
            detail: {
              company_name: tokenResponse.company_name,
              company_logo: tokenResponse.company_logo_url,
            }
          }));

          // 6. 상태 업데이트
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            accessibleMenus: menus,
            accessiblePaths: paths,
            authorizedPaths: authorized ?? [],
            authorizedPathsLoaded: authorized !== null,
            accessibleWidgets: widgets,
            passwordChangeRequired: (user as any).password_change_required || false,
          });

          // 7. React Query 캐시 초기화 (다른 기업 데이터 제거)
          queryClient.clear();

          // 7. Push 알림 재등록 (새 기업 토픽으로)
          if (isEmployee(user.user_type)) {
            await initializePush();
          }

          // 8. Native 토큰 저장 (모바일 앱인 경우)
          await saveTokensToNative(tokenResponse.access_token, tokenResponse.refresh_token);
        } catch (error) {
          console.error('[authStore] Company switch failed:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : "기업 전환에 실패했습니다.",
          });
          throw error;
        }
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        accessibleMenus: state.accessibleMenus,
        accessiblePaths: state.accessiblePaths,
        accessibleWidgets: state.accessibleWidgets,
        passwordChangeRequired: state.passwordChangeRequired,
      }),
    }
  )
);

// 세션 만료(갱신 401/403·기기 차단 등) 통지 → 스토어 초기화 → ProtectedRoute 가 /login 으로 이동.
// api.ts 는 authStore 를 import 할 수 없으므로(순환) 이벤트로 연결한다.
if (typeof window !== "undefined") {
  window.addEventListener(AUTH_EXPIRED_EVENT, () => {
    useAuthStore.setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      accessibleMenus: [],
      accessiblePaths: [],
      authorizedPaths: [],
      authorizedPathsLoaded: false,
      accessibleWidgets: [],
      passwordChangeRequired: false,
      documentSubmissionRequired: false,
      dailyReportRequired: false,
      noCompany: false,
      noPermissionGroup: false,
    });
    // 이전 사용자 데이터가 다음 로그인에 남지 않도록 (라우팅이 끝난 다음 틱에 정리)
    setTimeout(() => queryClient.clear(), 0);
  });
}

// Initialize auth on app load
if (typeof window !== "undefined") {
  useAuthStore.getState().initialize();
}
